import React from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@shared/lib/utils';

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      {subtitle ? <p className="text-sm font-medium text-muted-foreground mt-1">{subtitle}</p> : null}
    </div>
    {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
  </div>
);

export const StatusPill = ({ tone = 'neutral', label, className }) => {
  const tones = {
    success: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
    warning: 'bg-primary/15 text-primary border-primary/30',
    danger: 'bg-rose-500/15 text-rose-700 border-rose-500/30',
    destructive: 'bg-rose-500/15 text-rose-700 border-rose-500/30',
    info: 'bg-primary/10 text-primary border-primary/30',
    neutral: 'bg-muted text-muted-foreground border-border',
    default: 'bg-muted text-muted-foreground border-border',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide',
        tones[tone] || tones.neutral,
        className,
      )}
    >
      {label}
    </span>
  );
};

export const ProjectTabBar = ({ projectId, isProjectLead = false }) => {
  const base = `/portal/projects/${projectId}`;

  const tabs = [
    { label: 'Overview',    to: base,                   end: true },
    { label: 'Setup Guide', to: `${base}/setup` },
    // Team tab only visible to project leads (they manage team members)
    ...(isProjectLead ? [{ label: 'Team', to: `${base}/team` }] : []),
    { label: 'Documents',   to: `${base}/documents` },
    { label: 'Digital Badge', to: '/portal/digital-badge' },
  ];

  const scrollTop = () => window.scrollTo({ top: 0 });

  return (
    <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-4 lg:-mx-8 px-4 lg:px-8 -mt-6 mb-4 flex items-center border-b border-border overflow-x-auto">
      <NavLink
        to="/portal/inductions"
        onClick={scrollTop}
        className="flex items-center gap-1.5 pr-4 mr-2 border-r border-border h-11 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Invites
      </NavLink>

      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          onClick={scrollTop}
          className={({ isActive }) =>
            cn(
              'px-3 h-11 flex items-center text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  );
};

export const MetricCard = ({ label, value, helper, tone = 'neutral' }) => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <StatusPill tone={tone} label={tone === 'neutral' ? 'Status' : tone} />
    </div>
    <p className="mt-3 text-2xl font-semibold text-foreground">{value}</p>
    {helper ? <p className="mt-2 text-xs text-muted-foreground">{helper}</p> : null}
  </div>
);

export const SectionCard = ({ title, subtitle, children, className }) => (
  <div className={cn('rounded-xl border border-border bg-card p-5 shadow-sm', className)}>
    {title ? (
      <div className="mb-4">
        <h3 className="text-base font-bold text-foreground">{title}</h3>
        {subtitle ? <p className="text-sm font-medium text-muted-foreground mt-1">{subtitle}</p> : null}
      </div>
    ) : null}
    {children}
  </div>
);

export const EmptyState = ({ title, description, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 px-6 py-10 text-center">
    {Icon ? <Icon className="h-8 w-8 text-muted-foreground" /> : null}
    <h4 className="mt-3 text-sm font-bold text-foreground">{title}</h4>
    {description ? <p className="mt-1 text-sm font-medium text-muted-foreground max-w-sm">{description}</p> : null}
  </div>
);

export const InfoRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-border/80 py-2 last:border-b-0">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="text-sm font-semibold text-foreground">{value}</span>
  </div>
);

/**
 * InviteTabBar — horizontal tab strip shown at the top of invite detail pages.
 * Replaces the second contextual sidebar.
 *
 * Props:
 *   submissionId  — UUID of the induction_submission
 *   inviteStatus  — invite status string; shows Digital Badge tab only when 'approved'
 */
export const InviteTabBar = ({ submissionId, inviteStatus }) => {
  const base = `/portal/inductions/${submissionId}`;

  const tabs = [
    { label: 'Overview',    to: base,                   end: true },
    { label: 'Setup Guide', to: `${base}/setup` },
    { label: 'Documents',   to: `${base}/documents` },
    ...(inviteStatus === 'approved' ? [{ label: 'Digital Badge', to: '/portal/digital-badge' }] : []),
  ];

  const scrollTop = () => window.scrollTo({ top: 0 });

  return (
    <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-4 lg:-mx-8 px-4 lg:px-8 -mt-6 mb-4 flex items-center border-b border-border overflow-x-auto">
      <NavLink
        to="/portal/inductions"
        onClick={() => { sessionStorage.removeItem('portalActiveInductionId'); scrollTop(); }}
        className="flex items-center gap-1.5 pr-4 mr-2 border-r border-border h-11 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Invites
      </NavLink>

      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          onClick={scrollTop}
          className={({ isActive }) =>
            cn(
              'px-3 h-11 flex items-center text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  );
};
